import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, doc, updateDoc, getDoc, addDoc, deleteDoc, writeBatch, orderBy, where, getDocs } from './firestore-compat';
import AddQualidadeTaskManualModal from './AddQualidadeTaskManualModal';
import { notifyNewTaskAssigned } from './utils/taskNotificationUtils.js';
import './TarefasHomecare.css';

/** Formata "YYYY-MM-DD" → "DD/MM/YYYY" */
function formatDateBR(dateStr) {
    if (!dateStr) return '--';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
}

function TarefasQualidade() {
    const [tasks, setTasks] = useState([]);
    const [auditoresConfig, setAuditoresConfig] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [isAlarmActive, setIsAlarmActive] = useState(false);
    const [showAlarmPopup, setShowAlarmPopup] = useState(false);
    const [acknowledgedLateTaskIds, setAcknowledgedLateTaskIds] = useState([]);
    const [currentTime, setCurrentTime] = useState(new Date());
    
    // Filtros
    const [filterAuditor, setFilterAuditor] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, pendente, feito
    
    const [userRole, setUserRole] = useState('leitor');
    const [userName, setUserName] = useState('');
    
    const currentUser = auth.currentUser;

    useEffect(() => {
        const fetchUser = async () => {
            if (currentUser?.email) {
                const q = query(collection(db, 'users'), where('email', '==', currentUser.email));
                const snap = await getDocs(q);
                if (!snap.empty) {
                    const d = snap.docs[0].data();
                    const role = Array.isArray(d.roles) && d.roles.length > 0 ? d.roles[0] : (d.role || 'leitor');
                    setUserRole(role);
                    setUserName(d.name || currentUser.email);
                }
            }
        };
        fetchUser();

        // Load auditors
        const unsubAuditors = onSnapshot(query(collection(db, 'auditors_config'), orderBy('name')), (snap) => {
            setAuditoresConfig(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // Load tasks
        const unsubTasks = onSnapshot(query(collection(db, 'qualidade_tarefas')), (snap) => {
            setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubAuditors(); unsubTasks(); };
    }, [currentUser]);

    // Alarm Checker loop
    useEffect(() => {
        const interval = setInterval(() => {
            let shouldAlarm = false;
            const now = new Date();
            
            const currentLateTasks = [];
            tasks.forEach(t => {
                if (t.status !== 'Feito' && t.data && t.horario) {
                    const slaDate = new Date(`${t.data}T${t.horario}:00`);
                    if (now > slaDate) {
                        shouldAlarm = true;
                        currentLateTasks.push(t);
                    }
                }
            });

            setIsAlarmActive(shouldAlarm);

            // Verifica se há novas tarefas atrasadas não reconhecidas
            const unacknowledged = currentLateTasks.filter(t => !acknowledgedLateTaskIds.includes(t.id));
            if (unacknowledged.length > 0) {
                setShowAlarmPopup(true);
            }

        }, 10000); // Check every 10s
        
        return () => clearInterval(interval);
    }, [tasks, acknowledgedLateTaskIds]);

    // Timer loop for countdowns
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const handleDismissAlarm = () => {
        const currentLateIds = tasks.filter(t => isTaskLate(t)).map(t => t.id);
        setAcknowledgedLateTaskIds(prev => [...new Set([...prev, ...currentLateIds])]);
        setShowAlarmPopup(false);
    };

    const handleAddManualTask = async (newTask, agendarNaAgenda) => {
        if (!agendarNaAgenda) {
            await addDoc(collection(db, 'qualidade_tarefas'), { ...newTask, status: 'Pendente', reagendadoVezes: 0 });
        } else {
            const batch = writeBatch(db);
            const tarefaRef = doc(collection(db, 'qualidade_tarefas'));
            const agendaRef = doc(collection(db, 'auditorias'));
            batch.set(tarefaRef, { ...newTask, status: 'Pendente', reagendadoVezes: 0, agendaId: agendaRef.id });
            batch.set(agendaRef, { ...newTask, descricao: newTask.titulo, auditoria: newTask.titulo, categoria: 'Tarefa Interna', status: 'Agendado', reagendadoVezes: 0, tarefaId: tarefaRef.id });
            await batch.commit();
        }
        
        if (newTask.auditores && newTask.auditores.length > 0) {
            notifyNewTaskAssigned(newTask.titulo, newTask.auditores, userName, 'Tarefas da Qualidade');
        } else if (newTask.auditor) {
            notifyNewTaskAssigned(newTask.titulo, newTask.auditor, userName, 'Tarefas da Qualidade');
        }
    };

    const handleUpdateManualTask = async (originalTask, updatedData) => {
        const batch = writeBatch(db);
        const taskRef = doc(db, 'qualidade_tarefas', originalTask.id);
        let reagendadoVezes = originalTask.reagendadoVezes || 0;
        
        if (originalTask.data !== updatedData.data || originalTask.horario !== updatedData.horario) {
            reagendadoVezes++;
            if (!originalTask.slaOriginal) {
                updatedData.slaOriginal = {
                    data: originalTask.data,
                    horario: originalTask.horario
                };
            }
            updatedData.slaAjustadoPor = userName || 'desconhecido';
            updatedData.slaAjustadoEm = new Date().toISOString();
        }
        
        batch.update(taskRef, { ...updatedData, reagendadoVezes });
        
        if (originalTask.agendaId) {
            batch.update(doc(db, 'auditorias', originalTask.agendaId), {
                data: updatedData.data, horario: updatedData.horario,
                auditor: updatedData.auditor, auditores: updatedData.auditores,
                descricao: updatedData.titulo, auditoria: updatedData.titulo, reagendadoVezes
            });
        }
        await batch.commit();
        
        if (updatedData.auditores || updatedData.auditor) {
            const oldAuditores = originalTask.auditores || (originalTask.auditor ? [originalTask.auditor] : []);
            const newAuditores = updatedData.auditores || (updatedData.auditor ? [updatedData.auditor] : []);
            const addedAuditores = newAuditores.filter(a => !oldAuditores.includes(a));
            if (addedAuditores.length > 0) {
                notifyNewTaskAssigned(updatedData.titulo || originalTask.titulo, addedAuditores, userName, 'Tarefas da Qualidade');
            }
        }
    };

    const handleDeleteManualTask = async (taskId) => {
        try {
            const taskRef = doc(db, 'qualidade_tarefas', taskId);
            const taskDoc = await getDoc(taskRef);
            if (taskDoc.exists()) {
                const batch = writeBatch(db);
                if (taskDoc.data().agendaId) {
                    batch.delete(doc(db, 'auditorias', taskDoc.data().agendaId));
                }
                batch.delete(taskRef);
                await batch.commit();
            } else {
                alert('Tarefa não encontrada no banco de dados.');
            }
        } catch (err) {
            console.error('Erro ao excluir tarefa:', err);
            alert('Ocorreu um erro ao excluir a tarefa: ' + err.message);
        }
    };

    const handleToggleStatus = async (task) => {
        const newStatus = task.status === 'Feito' ? 'Pendente' : 'Feito';
        const batch = writeBatch(db);
        const taskRef = doc(db, 'qualidade_tarefas', task.id);
        const updateData = {
            status: newStatus,
            dataConclusao: newStatus === 'Feito' ? new Date().toISOString().split('T')[0] : null
        };
        batch.update(taskRef, updateData);
        if (task.agendaId) batch.update(doc(db, 'auditorias', task.agendaId), updateData);
        await batch.commit();
    };

    const openNewModal = () => {
        setEditingTask(null);
        setIsModalOpen(true);
    };

    // Computar KPIs (Tarefas antigas sem urgencyLevel recebem um padrão para não sumirem)
    const kpis = {
        urgente_importante: tasks.filter(t => (t.urgencyLevel === 'urgente_importante') && t.status !== 'Feito').length,
        nao_urgente_importante: tasks.filter(t => (t.urgencyLevel === 'nao_urgente_importante') && t.status !== 'Feito').length,
        urgente_nao_importante: tasks.filter(t => (t.urgencyLevel === 'urgente_nao_importante') && t.status !== 'Feito').length,
        nao_urgente_nao_importante: tasks.filter(t => (!t.urgencyLevel || t.urgencyLevel === 'nao_urgente_nao_importante') && t.status !== 'Feito').length,
    };

    const getAuditorsString = (task) => {
        const auds = task.auditores || (task.auditor ? [task.auditor] : []);
        return auds.length > 0 ? auds.join(', ') : 'Desconhecido';
    };

    // Aplicar Filtros
    let filteredTasks = tasks;
    if (filterAuditor) {
        filteredTasks = filteredTasks.filter(t => {
            const auds = t.auditores || (t.auditor ? [t.auditor] : []);
            return auds.includes(filterAuditor);
        });
    }
    if (filterStatus === 'pendente') {
        filteredTasks = filteredTasks.filter(t => t.status !== 'Feito');
    } else if (filterStatus === 'feito') {
        filteredTasks = filteredTasks.filter(t => t.status === 'Feito');
    }

    // Sort by SLA date, safely handling tasks without data/horario
    filteredTasks.sort((a, b) => {
        const dateA = (a.data && a.horario) ? new Date(`${a.data}T${a.horario}:00`).getTime() : Infinity;
        const dateB = (b.data && b.horario) ? new Date(`${b.data}T${b.horario}:00`).getTime() : Infinity;
        return dateA - dateB;
    });

    const isTaskLate = (task) => {
        if (task.status === 'Feito') return false;
        if (!task.data || !task.horario) return false;
        return new Date() > new Date(`${task.data}T${task.horario}:00`);
    };

    const lateTasksList = tasks.filter(t => isTaskLate(t) && !acknowledgedLateTaskIds.includes(t.id));

    const getClosestTask = (urgencyLevel) => {
        const pendingTasks = tasks.filter(t => {
            const taskLevel = t.urgencyLevel || 'nao_urgente_nao_importante';
            return taskLevel === urgencyLevel && t.status !== 'Feito' && t.data && t.horario;
        });
        if (pendingTasks.length === 0) return null;
        
        // Ordenar pela data do SLA
        pendingTasks.sort((a, b) => {
            return new Date(`${a.data}T${a.horario}:00`).getTime() - new Date(`${b.data}T${b.horario}:00`).getTime();
        });
        return pendingTasks[0];
    };

    const getTimeRemainingString = (task) => {
        if (!task) return '--';
        if (task.status === 'Feito') return 'Concluído';
        if (!task.data || !task.horario) return '--';
        const slaDate = new Date(`${task.data}T${task.horario}:00`);
        const diffMs = slaDate - currentTime;
        
        const isLate = diffMs <= 0;
        const absDiff = Math.abs(diffMs);
        
        const diffHrs = Math.floor(absDiff / (1000 * 60 * 60));
        const diffMins = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
        const diffSecs = Math.floor((absDiff % (1000 * 60)) / 1000);
        
        let res = isLate ? 'Atrasado ' : 'Falta ';
        if (diffHrs > 0) res += `${diffHrs}h `;
        res += `${diffMins}m ${diffSecs}s`;
        return res;
    };

    return (
        <div className="tarefas-homecare-container" style={{ padding: '20px', minHeight: '100vh', transition: 'background-color 0.5s' }}>
            
            {/* POPUP DE ALARME (SLA ESTOURADO) */}
            {showAlarmPopup && (
                <div className="reminder-urgent-overlay" style={{ zIndex: 9999 }}>
                    <div className="reminder-urgent-container" style={{ 
                        width: '100vw', 
                        height: '100vh', 
                        maxWidth: 'none', 
                        borderRadius: '0', 
                        margin: '0', 
                        padding: '50px 5%', 
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}>
                        <div className="reminder-urgent-bar reminder-urgent-bar-top"></div>
                        <div className="reminder-urgent-bar reminder-urgent-bar-bottom"></div>
                        <div className="reminder-urgent-icon" style={{ marginTop: '20px' }}>🚨</div>
                        
                        <h1 className="reminder-urgent-title">
                            SLA ESTOURADO!
                        </h1>

                        <div className="reminder-urgent-count">
                            <span className="reminder-urgent-count-number">{lateTasksList.length}</span>
                            <span className="reminder-urgent-count-label">
                                tarefa(s) ultrapassou o tempo limite.
                            </span>
                        </div>

                        <div className="reminder-urgent-list" style={{ 
                            flexGrow: 1, 
                            maxHeight: 'none',
                            overflowY: 'auto', 
                            overflowX: 'hidden',
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
                            gap: '15px',
                            padding: '20px',
                            alignContent: 'start'
                        }}>
                            {lateTasksList.map((item) => (
                                <div key={item.id} className="reminder-urgent-item" style={{ margin: 0, padding: '12px' }}>
                                    <span className="reminder-urgent-item-time" style={{ minWidth: '80px', fontSize: '0.95rem' }}>
                                    ⏰ {item.horario} — {formatDateBR(item.data)}
                                    </span>
                                    <span className="reminder-urgent-item-text" style={{ fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.titulo}
                                    </span>
                                    <span className="reminder-urgent-item-meta" style={{ fontSize: '0.85rem', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                                        👤 {getAuditorsString(item)}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="reminder-urgent-actions" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <button className="reminder-urgent-btn-dismiss" onClick={handleDismissAlarm}>
                                ✓ Ciente (Silenciar)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 style={{ margin: 0, color: '#333' }}>Tarefas da Qualidade (Matriz de Prioridades)</h1>
                <button 
                    onClick={openNewModal}
                    style={{ padding: '10px 20px', backgroundColor: '#00897b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    + Nova Tarefa
                </button>
            </div>

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                <div style={{ backgroundColor: '#c62828', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Urgente e Importante</h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{kpis.urgente_importante}</div>
                    <small>Pendentes</small>
                    <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', background: 'rgba(255,255,255,0.2)', padding: '5px', borderRadius: '4px' }}>
                        ⏱️ Próximo: {getTimeRemainingString(getClosestTask('urgente_importante'))}
                    </div>
                </div>
                <div style={{ backgroundColor: '#fbc02d', color: '#333', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Não Urgente / Importante</h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{kpis.nao_urgente_importante}</div>
                    <small>Pendentes</small>
                    <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', background: 'rgba(0,0,0,0.1)', padding: '5px', borderRadius: '4px' }}>
                        ⏱️ Próximo: {getTimeRemainingString(getClosestTask('nao_urgente_importante'))}
                    </div>
                </div>
                <div style={{ backgroundColor: '#f57c00', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Urgente / Não Importante</h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{kpis.urgente_nao_importante}</div>
                    <small>Pendentes</small>
                    <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', background: 'rgba(255,255,255,0.2)', padding: '5px', borderRadius: '4px' }}>
                        ⏱️ Próximo: {getTimeRemainingString(getClosestTask('urgente_nao_importante'))}
                    </div>
                </div>
                <div style={{ backgroundColor: '#2e7d32', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Não Urg / Não Imp</h3>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{kpis.nao_urgente_nao_importante}</div>
                    <small>Pendentes</small>
                    <div style={{ marginTop: '10px', fontSize: '0.85rem', fontWeight: 'bold', background: 'rgba(255,255,255,0.2)', padding: '5px', borderRadius: '4px' }}>
                        ⏱️ Próximo: {getTimeRemainingString(getClosestTask('nao_urgente_nao_importante'))}
                    </div>
                </div>
            </div>

            {/* Filtros */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', backgroundColor: '#fff', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>Filtrar por Auditor:</label>
                    <select value={filterAuditor} onChange={e => setFilterAuditor(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', minWidth: '200px' }}>
                        <option value="">Todos</option>
                        {auditoresConfig.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>Status da Tarefa:</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', minWidth: '150px' }}>
                        <option value="all">Todas</option>
                        <option value="pendente">Apenas Pendentes</option>
                        <option value="feito">Concluídas</option>
                    </select>
                </div>
            </div>

            {/* Lista de Tarefas */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredTasks.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#888', backgroundColor: '#fff', borderRadius: '8px' }}>
                        Nenhuma tarefa encontrada com os filtros atuais.
                    </div>
                ) : (
                    filteredTasks.map(task => {
                        const late = isTaskLate(task);
                        return (
                            <div key={task.id} style={{ 
                                display: 'flex', alignItems: 'center', gap: '15px', 
                                backgroundColor: '#fff', padding: '15px', borderRadius: '8px',
                                borderLeft: late ? '6px solid #dc3545' : '6px solid #28a745',
                                boxShadow: late ? '0 0 10px rgba(220,53,69,0.3)' : '0 2px 4px rgba(0,0,0,0.05)',
                                opacity: task.status === 'Feito' ? 0.7 : 1,
                                transition: 'all 0.3s'
                            }}>
                                <input 
                                    type="checkbox" 
                                    checked={task.status === 'Feito'}
                                    onChange={() => handleToggleStatus(task)}
                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                />
                                <div style={{ flexGrow: 1 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: task.status === 'Feito' ? '#888' : '#333', textDecoration: task.status === 'Feito' ? 'line-through' : 'none' }}>
                                        {task.titulo}
                                    </div>
                                    <div style={{ display: 'flex', gap: '20px', fontSize: '0.85rem', color: '#666', marginTop: '6px' }}>
                                        <span style={{ fontWeight: 'bold' }}>👤 {getAuditorsString(task)} {task.unidade ? `(${task.unidade})` : ''}</span>
                                        <span style={{ color: late ? '#dc3545' : '#666', fontWeight: late ? 'bold' : 'normal', backgroundColor: late ? '#ffe6e6' : 'transparent', padding: late ? '2px 6px' : '0', borderRadius: '4px', display: 'inline-block' }}>
                                            ⏰ SLA: <strong>{formatDateBR(task.data)}</strong> às <strong>{task.horario}</strong>
                                            {late && <span style={{ marginLeft: '6px', fontWeight: 'bold' }}>(EM ATRASO)</span>}
                                        </span>

                                        {/* SLA Ajustado */}
                                        {task.slaOriginal && (
                                            <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '3px' }}>
                                                SLA anterior: {formatDateBR(task.slaOriginal.data)} às {task.slaOriginal.horario}
                                            </div>
                                        )}
                                    </div>
                                    {task.status !== 'Feito' && (
                                        <div style={{ marginTop: '8px', fontSize: '0.8rem', fontWeight: 'bold', color: late ? '#dc3545' : '#00897b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            ⏱️ {getTimeRemainingString(task)}
                                        </div>
                                    )}

                                    {/* Comentários */}
                                    {task.comments && task.comments.length > 0 && (
                                        <div style={{ marginTop: '8px', padding: '6px 10px', background: '#f0faf8', borderRadius: '6px', border: '1px solid #b2dfdb', fontSize: '0.83rem', color: '#37474f' }}>
                                            💬 <em>{task.comments[task.comments.length - 1].text}</em>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button 
                                        onClick={() => { setEditingTask(task); setIsModalOpen(true); }}
                                        style={{ padding: '6px 12px', border: '1px solid #ccc', backgroundColor: '#f8f9fa', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', color: '#555' }}
                                    >
                                        Editar
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <AddQualidadeTaskManualModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onAddTask={handleAddManualTask}
                onUpdateTask={handleUpdateManualTask}
                onDeleteTask={handleDeleteManualTask}
                editingTask={editingTask}
                userRole={userRole}
                userName={userName}
                auditoresConfig={auditoresConfig}
            />
        </div>
    );
}

export default TarefasQualidade;